import { useState } from "react";
import { Section, Reveal, Overline } from "./primitives";
import { SERVICE_OPTIONS, ENQUIRY_TYPES, waLink, CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_RAW, OFFICE_ADDRESS, MAP_EMBED_URL } from "../../data";
import { createLead } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Mail, Phone, MapPin, MessageCircle, Loader2, ArrowUpRight } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", enquiry_type: "New project", service: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please add your name and email."); return; }
    setLoading(true);
    try {
      await createLead(form);
      toast.success("Thanks! Our team will reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", enquiry_type: "New project", service: "", budget: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <Section id="contact" className="noise-bg">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>Let&apos;s Talk</Overline>
            <h2 className="mt-6 font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Book your free <span className="text-gradient">growth consultation</span>.
            </h2>
            <p className="mt-6 text-muted-foreground max-w-md">
              Tell us where you want to grow. We&apos;ll come back with a clear, no-obligation plan within 24 hours.
            </p>
            <div className="mt-10 space-y-4">
              {[
                { icon: Mail, label: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
                { icon: Phone, label: CONTACT_PHONE, href: `tel:${CONTACT_PHONE_RAW}` },
                { icon: MapPin, label: "Level 12, Growth Tower — 18 cities", href: "#contact" },
              ].map((c) => (
                <a key={c.label} href={c.href} className="flex items-center gap-4 group">
                  <span className="h-11 w-11 grid place-items-center rounded-full border border-border/15 text-primary group-hover:bg-brand-gradient group-hover:text-white group-hover:border-transparent transition-colors">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-medium">{c.label}</span>
                </a>
              ))}
            </div>
            <a
              data-testid="contact-whatsapp"
              href={waLink()}
              target="_blank" rel="noreferrer"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-border/15 px-6 py-3 text-sm font-semibold hover:border-primary hover:text-primary transition-colors"
            >
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="rounded-[2rem] border border-border/10 bg-card p-8 md:p-10 space-y-5">
              <div className="space-y-2">
                <Label>I'm reaching out about</Label>
                <div className="flex flex-wrap gap-2">
                  {ENQUIRY_TYPES.map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`enquiry-${t.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => set("enquiry_type", t)}
                      className={`rounded-full px-5 py-2 text-sm font-medium border transition-colors ${form.enquiry_type === t ? "bg-brand-gradient text-white border-transparent" : "border-border/15 text-muted-foreground hover:text-foreground hover:border-primary/40"}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                {form.enquiry_type === "Career" && (
                  <a href="/careers" data-testid="career-hint" className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
                    See our open roles <ArrowUpRight className="h-4 w-4" />
                  </a>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name">Full name *</Label>
                  <Input data-testid="contact-name" id="name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" className="rounded-xl bg-background/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input data-testid="contact-email" id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@company.com" className="rounded-xl bg-background/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input data-testid="contact-phone" id="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 ..." className="rounded-xl bg-background/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Input data-testid="contact-company" id="company" value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Acme Inc." className="rounded-xl bg-background/50 h-12" />
                </div>
                <div className="space-y-2">
                  <Label>Service</Label>
                  <Select value={form.service} onValueChange={(v) => set("service", v)}>
                    <SelectTrigger data-testid="contact-service" className="rounded-xl bg-background/50 h-12"><SelectValue placeholder="Select a service" /></SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Monthly budget</Label>
                  <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                    <SelectTrigger data-testid="contact-budget" className="rounded-xl bg-background/50 h-12"><SelectValue placeholder="Select a range" /></SelectTrigger>
                    <SelectContent>
                      {["< $2.5k", "$2.5k – $6k", "$6k – $15k", "$15k+"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">What do you want to grow?</Label>
                <Textarea data-testid="contact-message" id="message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about your goals..." className="rounded-xl bg-background/50 min-h-[120px]" />
              </div>
              <Button data-testid="contact-submit" type="submit" disabled={loading} className="w-full rounded-full h-13 py-6 bg-brand-gradient text-white border-0 hover:opacity-90 text-base">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Get Free Consultation <ArrowUpRight className="ml-1 h-5 w-5" /></>}
              </Button>
            </form>
          </Reveal>
        </div>
      </div>

      {/* Office map */}
      <Reveal delay={0.1}>
        <div className="mt-8 rounded-[2rem] overflow-hidden border border-border/10 bg-card grid lg:grid-cols-2">
          <iframe
            data-testid="office-map"
            title="VYFT office location"
            src={MAP_EMBED_URL}
            className="w-full h-[320px] lg:h-full min-h-[320px] grayscale contrast-125"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="p-8 md:p-10 flex flex-col justify-center">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-primary shrink-0 mt-1" />
              <div>
                <div className="font-display text-xl font-bold">VYFT Agency — Head Office</div>
                <div className="mt-1 text-muted-foreground">{OFFICE_ADDRESS}</div>
              </div>
            </div>
            <a
              data-testid="get-directions"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(OFFICE_ADDRESS)}`}
              target="_blank" rel="noreferrer"
              className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border border-border/15 px-5 py-2.5 text-sm font-semibold hover:border-primary hover:text-primary transition-colors"
            >
              <MapPin className="h-4 w-4" /> Get directions
            </a>
          </div>
        </div>
      </Reveal>
    </Section>
  );
}
